import { useMutation, useQueryClient } from '@tanstack/react-query';
import snakecaseKeys from 'snakecase-keys';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

type AddSiteIdentifierFromProposalResponse = {
  code: string;
  message: string;
  requestId: UUID | undefined;
};

type ProposalIdentifierSiteData = {
  blacklisted: boolean;
  ignoreAddressInfo: boolean;
  proposalUuid: UUID;
  updateDlns: boolean;
};

type ProposalIdentifierSiteArguments = {
  siteData: ProposalIdentifierSiteData;
  siteId: UUID;
};

/**
 * Adds a new site identifier to a given site based on a given site identifier proposal.
 * This allows adding a blacklisted site identifier.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/add_site_identifier_proposal_to_site_site__site_uuid__identifier_proposal_post
 *
 * @param {UUID} siteId - The UUID of the site.
 * @param {ProposalIdentifierSiteData} siteData - Data related to the site identifier proposal.
 * @returns {Promise<AddSiteIdentifierFromProposalResponse>} The response from the API after the POST request.
 */
export const addSiteIdentifierFromProposal = async (
  siteId: UUID,
  siteData: ProposalIdentifierSiteData,
) => {
  const { blacklisted, proposalUuid, updateDlns, ignoreAddressInfo } = siteData;
  const body = {
    proposalUuid,
  };
  const searchParams = {
    blacklisted,
    updateDlns,
    ignoreAddressInfo,
  };

  const response = await vestigasApi
    .post(ENDPOINT.SITE.PROPOSAL_IDENTIFIER(siteId), {
      json: body,
      searchParams: snakecaseKeys(searchParams),
    })
    .json<AddSiteIdentifierFromProposalResponse>();

  return response;
};

/**
 * Custom hook for adding a site identifier from a proposal using mutations.
 *
 * @param {Object} options - Additional options for the mutation.
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationAddSiteIdentifierFromProposal = (
  options?: Parameters<typeof useMutation>[0],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ siteId, siteData }: ProposalIdentifierSiteArguments) =>
      addSiteIdentifierFromProposal(siteId, siteData),
    onSuccess(_data, { siteId }) {
      // Refresh all site data after applying an identifier
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.getAll({}),
      });
      // Refresh the updated site after applying an identifier
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.get(siteId),
      });
      // Refresh proposals after applying one as identifier
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.getSiteIdentifierProposals(),
      });
    },
    ...options,
  });
};
